import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { bookingService } from '../services/api';
import { FiSearch, FiInfo, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './Bookings.css';

/** Must match admin_service REST_FRAMEWORK PAGE_SIZE */
const PAGE_SIZE = 10;

const STATUS_LABELS = {
  pending: 'Pending',
  payment_completed: 'Payment completed',
  visited: 'Visited',
  cancelled: 'Cancelled',
};

function parseMoney(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function formatInr(amount) {
  const n = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  return `₹ ${n.toFixed(2)}`;
}

/** Split stored total GST into CGST / SGST (9% + 9% model used at checkout). */
function splitCgstSgst(taxAmount) {
  const tax = Math.max(0, taxAmount);
  const half = Math.round((tax / 2) * 100) / 100;
  const cgst = half;
  const sgst = Math.round((tax - half) * 100) / 100;
  return { cgst, sgst, gstTotal: tax };
}

function resolveBookingAmounts(booking) {
  const total = parseMoney(booking.total_amount) ?? 0;
  let subtotal = parseMoney(booking.subtotal);
  let taxAmount = parseMoney(booking.tax_amount);
  let convenienceFee = parseMoney(booking.convenience_fee);

  if (convenienceFee === null) convenienceFee = 0;
  if (taxAmount === null) taxAmount = 0;

  if (subtotal === null) {
    subtotal = Math.max(0, total - taxAmount - convenienceFee);
  }

  const { cgst, sgst, gstTotal } = splitCgstSgst(taxAmount);

  return {
    subtotal,
    convenienceFee,
    taxAmount,
    cgst,
    sgst,
    gstTotal,
    total,
  };
}

function BookingAmountBreakdownModal({ booking, onClose }) {
  const amounts = useMemo(() => resolveBookingAmounts(booking), [booking]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="bookings-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bookings-amount-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bookings-amount-breakdown-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bookings-amount-modal__header">
          <h2 id="bookings-amount-breakdown-title">Amount breakdown</h2>
          <p className="bookings-amount-modal__ref">{booking.booking_id}</p>
        </div>

        <div className="bookings-amount-modal__body">
          <div className="bookings-amount-block">
            <div className="bookings-amount-row">
              <span className="bookings-amount-row__label">Base amount</span>
              <span className="bookings-amount-row__value">{formatInr(amounts.subtotal)}</span>
            </div>
            <p className="bookings-amount-block__hint">Ticket and add-ons before tax</p>
          </div>
          <div className="bookings-amount-row bookings-amount-row--compact">
            <span className="bookings-amount-row__label">Convenience fee</span>
            <span className="bookings-amount-row__value">{formatInr(amounts.convenienceFee)}</span>
          </div>
          <div className="bookings-amount-modal__section-label">GST</div>
          <div className="bookings-amount-row bookings-amount-row--compact">
            <span className="bookings-amount-row__label">CGST (9%)</span>
            <span className="bookings-amount-row__value">{formatInr(amounts.cgst)}</span>
          </div>
          <div className="bookings-amount-row bookings-amount-row--compact">
            <span className="bookings-amount-row__label">SGST (9%)</span>
            <span className="bookings-amount-row__value">{formatInr(amounts.sgst)}</span>
          </div>
          <div className="bookings-amount-row bookings-amount-row--subtotal">
            <span className="bookings-amount-row__label">Total GST</span>
            <span className="bookings-amount-row__value">{formatInr(amounts.gstTotal)}</span>
          </div>
          <div className="bookings-amount-row bookings-amount-row--total">
            <span className="bookings-amount-row__label">Final total</span>
            <span className="bookings-amount-row__value">{formatInr(amounts.total)}</span>
          </div>
        </div>

        <div className="bookings-amount-modal__footer">
          <button type="button" className="bookings-amount-modal__close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const DUMMY_BOOKINGS_BASE = [
  { id: 1, booking_id: 'BK-2024-001', visit_date: '2024-02-15', created_at: '2024-02-10T14:30:00Z', full_name: 'Rajesh Kumar', mobile_number: '+91 98765 43210', subtotal: '2117.80', convenience_fee: '40.00', tax_amount: '381.20', total_amount: '2539.00', status: 'payment_completed' },
  { id: 2, booking_id: 'BK-2024-002', visit_date: '2024-02-18', created_at: '2024-02-12T09:15:00Z', full_name: 'Priya Sharma', mobile_number: '+91 91234 56789', subtotal: '1571.61', convenience_fee: '40.00', tax_amount: '282.89', total_amount: '1894.50', status: 'pending' },
  { id: 3, booking_id: 'BK-2024-003', visit_date: '2024-02-20', created_at: '2024-02-14T16:45:00Z', full_name: 'Amit Patel', mobile_number: '+91 99887 66554', subtotal: '2711.86', convenience_fee: '40.00', tax_amount: '488.14', total_amount: '3240.00', status: 'visited' },
  { id: 4, booking_id: 'BK-2024-004', visit_date: '2024-02-22', created_at: '2024-02-16T11:00:00Z', full_name: 'Sneha Reddy', mobile_number: '+91 87654 32109', subtotal: '1355.08', convenience_fee: '40.00', tax_amount: '243.92', total_amount: '1639.00', status: 'cancelled' },
  { id: 5, booking_id: 'BK-2024-005', visit_date: '2024-02-25', created_at: '2024-02-18T08:20:00Z', full_name: 'Vikram Singh', mobile_number: '+91 76543 21098', subtotal: '2336.44', convenience_fee: '40.00', tax_amount: '420.56', total_amount: '2797.00', status: 'payment_completed' },
];

const DUMMY_BOOKINGS = [
  ...DUMMY_BOOKINGS_BASE,
  ...Array.from({ length: 18 }, (_, i) => {
    const n = 6 + i;
    return {
      id: n,
      booking_id: `BK-2024-${String(n).padStart(3, '0')}`,
      visit_date: '2024-03-05',
      created_at: '2024-02-22T12:00:00Z',
      full_name: `Demo guest ${n}`,
      mobile_number: `+91 90000 ${String(10000 + n).slice(-5)}`,
      subtotal: '1000.00',
      convenience_fee: '40.00',
      tax_amount: '180.00',
      total_amount: '1220.00',
      status: 'payment_completed',
    };
  }),
];

function BookingsPagination({
  page,
  totalCount,
  pageSize,
  onPageChange,
  disabled,
  paged,
  className,
  position = 'bottom',
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  const go = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== page) onPageChange(next);
  };

  const ariaPosition = position === 'top' ? 'top of table' : 'bottom of table';
  const rootClass = ['bookings-pagination', className].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      aria-label={`Bookings pagination (${ariaPosition})`}
    >
      <p className="bookings-pagination__summary">
        {totalCount === 0 ? (
          'No bookings to show'
        ) : paged ? (
          <>
            Showing <strong>{from}</strong>–<strong>{to}</strong> of <strong>{totalCount}</strong>
          </>
        ) : (
          <>
            Showing all <strong>{totalCount}</strong> {totalCount === 1 ? 'booking' : 'bookings'}
          </>
        )}
      </p>
      {paged && totalPages > 1 ? (
        <div className="bookings-pagination__controls">
          <button
            type="button"
            className="bookings-pagination__btn"
            onClick={() => go(page - 1)}
            disabled={disabled || page <= 1}
            aria-label="Previous page"
          >
            <FiChevronLeft aria-hidden />
            Previous
          </button>
          <span className="bookings-pagination__page">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            className="bookings-pagination__btn"
            onClick={() => go(page + 1)}
            disabled={disabled || page >= totalPages || totalCount === 0}
            aria-label="Next page"
          >
            Next
            <FiChevronRight aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [isDummyData, setIsDummyData] = useState(false);
  const [breakdownBooking, setBreakdownBooking] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paged, setPaged] = useState(true);
  const loadedOnceRef = useRef(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    visitDateFrom: '',
    visitDateTo: '',
    paymentDateFrom: '',
    paymentDateTo: '',
  });

  const updateFilters = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  const buildQueryParams = useCallback(() => {
    const params = { page };
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    if (filters.visitDateFrom) params.visit_date__gte = filters.visitDateFrom;
    if (filters.visitDateTo) params.visit_date__lte = filters.visitDateTo;
    if (filters.paymentDateFrom) params.created_at__gte = filters.paymentDateFrom;
    if (filters.paymentDateTo) params.created_at__lte = filters.paymentDateTo;
    return params;
  }, [filters, page]);

  const loadDummyPage = useCallback(() => {
    const start = (page - 1) * PAGE_SIZE;
    const slice = DUMMY_BOOKINGS.slice(start, start + PAGE_SIZE);
    setBookings(slice);
    setTotalCount(DUMMY_BOOKINGS.length);
    setIsDummyData(true);
    setPaged(true);
  }, [page]);

  const fetchBookings = useCallback(async () => {
    const showFullLoader = !loadedOnceRef.current;
    if (showFullLoader) setLoading(true);
    else setListLoading(true);
    try {
      const response = await bookingService.getAll(buildQueryParams());
      const body = response.data;

      if (body && typeof body.count === 'number' && Array.isArray(body.results)) {
        setBookings(body.results);
        setTotalCount(body.count);
        setIsDummyData(false);
        setPaged(true);
      } else if (Array.isArray(body)) {
        setBookings(body);
        setTotalCount(body.length);
        setIsDummyData(false);
        setPaged(false);
      } else {
        setBookings([]);
        setTotalCount(0);
        setIsDummyData(false);
        setPaged(true);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      loadDummyPage();
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
      setListLoading(false);
    }
  }, [buildQueryParams, loadDummyPage]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleCancel = async (id) => {
    if (isDummyData) return;
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        await bookingService.cancel(id);
        fetchBookings();
      } catch (error) {
        console.error('Error cancelling booking:', error);
      }
    }
  };

  const handleConfirm = async (id) => {
    if (isDummyData) return;
    try {
      await bookingService.confirm(id);
      fetchBookings();
    } catch (error) {
      console.error('Error confirming booking:', error);
    }
  };

  const handleMarkVisited = async (id) => {
    if (isDummyData) return;
    try {
      await bookingService.markVisited(id);
      fetchBookings();
    } catch (error) {
      console.error('Error marking as visited:', error);
    }
  };

  const getStatusLabel = (status) => STATUS_LABELS[status] || status;

  if (loading) {
    return <div className="loading">Loading bookings...</div>;
  }

  return (
    <div className="bookings-page">
      <div className="page-header">
        <h1>Bookings Management</h1>
      </div>

      <div className="filters-row">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search bookings..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={filters.status}
            onChange={(e) => updateFilters({ status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="payment_completed">Payment completed</option>
            <option value="visited">Visited</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="date-range-group">
          <label className="date-range-label">Visit Date</label>
          <div className="date-range-inputs">
            <input
              type="date"
              placeholder="From"
              value={filters.visitDateFrom}
              onChange={(e) => updateFilters({ visitDateFrom: e.target.value })}
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              placeholder="To"
              value={filters.visitDateTo}
              onChange={(e) => updateFilters({ visitDateTo: e.target.value })}
            />
          </div>
        </div>
        <div className="date-range-group">
          <label className="date-range-label">Payment Date</label>
          <div className="date-range-inputs">
            <input
              type="date"
              placeholder="From"
              value={filters.paymentDateFrom}
              onChange={(e) => updateFilters({ paymentDateFrom: e.target.value })}
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              placeholder="To"
              value={filters.paymentDateTo}
              onChange={(e) => updateFilters({ paymentDateTo: e.target.value })}
            />
          </div>
        </div>
      </div>

      {!loading ? (
        <BookingsPagination
          position="top"
          className="bookings-pagination--top"
          page={page}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          disabled={listLoading}
          paged={paged}
        />
      ) : null}

      <div className={`bookings-table-wrap${listLoading ? ' bookings-table-wrap--busy' : ''}`}>
        <div className="bookings-table-wrap__inner">
          <div className="bookings-table">
        <table>
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Visit Date</th>
              <th>Payment Date</th>
              <th>Customer</th>
              <th className="bookings-table__col-amount">Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">No bookings found</td>
              </tr>
            ) : (
              bookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{booking.booking_id}</td>
                  <td>{booking.visit_date ? new Date(booking.visit_date).toLocaleDateString() : '—'}</td>
                  <td>{booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '—'}</td>
                  <td>
                    <div>
                      <div>{booking.full_name}</div>
                      <div className="customer-mobile">{booking.mobile_number || booking.email || '—'}</div>
                    </div>
                  </td>
                  <td className="bookings-table__col-amount">
                    <div className="bookings-amount-cell">
                      <span className="bookings-amount-cell__value">
                        {formatInr(parseMoney(booking.total_amount) ?? 0)}
                      </span>
                      <button
                        type="button"
                        className="bookings-amount-info-btn"
                        aria-label={`View amount breakdown for ${booking.booking_id}`}
                        title="View breakdown"
                        onClick={() => setBreakdownBooking(booking)}
                      >
                        <FiInfo aria-hidden />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${booking.status}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {booking.status === 'pending' && (
                        <>
                          <button
                            className="btn-confirm"
                            onClick={() => handleConfirm(booking.id)}
                          >
                            Confirm
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={() => handleCancel(booking.id)}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {booking.status === 'payment_completed' && (
                        <>
                          <button
                            className="btn-visited"
                            onClick={() => handleMarkVisited(booking.id)}
                          >
                            Mark visited
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={() => handleCancel(booking.id)}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
          </div>
        </div>
      </div>

      {!loading ? (
        <BookingsPagination
          position="bottom"
          className="bookings-pagination--bottom"
          page={page}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          disabled={listLoading}
          paged={paged}
        />
      ) : null}

      {breakdownBooking ? (
        <BookingAmountBreakdownModal
          booking={breakdownBooking}
          onClose={() => setBreakdownBooking(null)}
        />
      ) : null}
    </div>
  );
};

export default Bookings;
